import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Textarea } from '../FormField';
import LoadingSpinner from '../LoadingSpinner';
import { useNotifications } from '../NotificationProvider';
import { reportSaveError, useFieldErrors } from '../forms/_shared';
import { getClientInvoiceById, recordClientInvoicePayment, ClientInvoice } from '../../services/ClientInvoiceService';
import { formatMoney } from '../../utils/currency';
import { clientInvoicePath } from '../../utils/accountRoutes';
import AccountFormPage from './AccountFormPage';

interface InvoicePaymentFormPageProps {
  invoiceId: string;
}

const LIST_PATH = '/invoices';

/** Record a payment against a client invoice, as a page of its own. */
export const InvoicePaymentFormPage: React.FC<InvoicePaymentFormPageProps> = ({ invoiceId }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const fieldErrors = useFieldErrors();

  const [invoice, setInvoice] = useState<ClientInvoice | null>(null);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    let cancelled = false;
    getClientInvoiceById(invoiceId)
      .then(inv => {
        if (cancelled) return;
        setInvoice(inv);
        setAmount(inv.balance_due > 0 ? String(inv.balance_due) : '');
      })
      .catch(err => {
        if (cancelled) return;
        showError(err instanceof Error ? err.message : 'Could not open this invoice.');
        navigate(LIST_PATH, { replace: true });
      });
    return () => { cancelled = true; };
  }, [invoiceId, navigate, showError]);

  if (!invoice) return <LoadingSpinner />;

  const back = clientInvoicePath(invoiceId);
  const parsedAmount = Number(amount);

  const handleSave = async () => {
    if (!(parsedAmount > 0) || saving) return;
    fieldErrors.clear();
    setSaving(true);
    try {
      await recordClientInvoicePayment(invoiceId, {
        amount: parsedAmount,
        method: method.trim() || undefined,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      showSuccess(`Payment recorded on ${invoice.invoice_number}.`);
      navigate(back);
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AccountFormPage
      view="invoice-payment-form"
      via={[{ label: invoice.invoice_number, to: back }]}
      title={`Record payment on ${invoice.invoice_number}`}
      onSave={handleSave}
      onCancel={() => navigate(back)}
      saveLabel="Record payment"
      canSave={parsedAmount > 0}
      saving={saving}
    >
      <p className="ap-note">Balance due: {formatMoney(invoice.balance_due, invoice.currency)}</p>
      <Input label="Amount" type="number" min={0} required error={fieldErrors.errorFor('amount')} value={amount} onChange={e => { setAmount(e.target.value); fieldErrors.clear('amount'); }} autoFocus />
      <div className="ap-field-row">
        <Input label="Method" error={fieldErrors.errorFor('method')} value={method} onChange={e => { setMethod(e.target.value); fieldErrors.clear('method'); }} placeholder="e.g. Bank transfer" />
        <Input label="Reference" error={fieldErrors.errorFor('reference')} value={reference} onChange={e => { setReference(e.target.value); fieldErrors.clear('reference'); }} placeholder="e.g. transaction ID" />
      </div>
      <Textarea label="Notes" error={fieldErrors.errorFor('notes')} value={notes} onChange={e => { setNotes(e.target.value); fieldErrors.clear('notes'); }} rows={3} />
    </AccountFormPage>
  );
};

export default InvoicePaymentFormPage;
